/**
 * TS-зеркало каркаса заказов (api-contract §3c). Единый источник ярлыков/каталога для фронта;
 * ключи этапов и канонические значения копируются ДОСЛОВНО из §3c и зашиты в CHECK БД.
 * Бэкенд хранит тот же stage-каталог (BE Go ↔ FE TS зеркалятся дословно).
 */
import type { StatusRole } from '@/design-system/tokens/status'
import type { AuditDetails, OrderDataStatus, Service, StageDTO } from '@/lib/api'

/** Определение этапа «Ход работ»: ключ (хранится в БД) + русский ярлык. */
export interface StageDef {
  key: string
  label: string
}

/**
 * Каталог этапов «Ход работ» по услуге (§3c, позиции 0..3). Порядок и ключи фиксированы;
 * при создании заказа все этапы сидируются строками `order_stages`.
 */
export const STAGES_BY_SERVICE: Record<Service, StageDef[]> = {
  sout: [
    { key: 'application', label: 'Заявка' },
    { key: 'contract', label: 'Договор' },
    { key: 'measuring', label: 'Замеры' },
    { key: 'expert_report', label: 'Экспертное заключение' },
  ],
  training: [
    { key: 'application', label: 'Заявка' },
    { key: 'contract', label: 'Договор' },
    { key: 'training', label: 'Обучение' },
    { key: 'final_document', label: 'Итоговый документ' },
  ],
  profrisk: [
    { key: 'application', label: 'Заявка' },
    { key: 'contract', label: 'Договор' },
    { key: 'assessment', label: 'Оценка' },
    { key: 'risk_maps', label: 'Карты рисков' },
  ],
  // Экология (ecology-order-stages, файл отдела экологии 28.09.2026): «Принят, Сбор данных, В работе,
  // Выполнен». У абонентского обслуживания этапа «Сбор данных» нет — сервер убирает его при подаче,
  // поэтому этапы конкретного заказа берутся из самого заказа (`order.stages`), а не отсюда.
  ecology: [
    { key: 'accepted', label: 'Принят' },
    { key: 'data_collection', label: 'Сбор данных' },
    { key: 'work', label: 'В работе' },
    { key: 'completed', label: 'Выполнен' },
  ],
}

/** Русские ярлыки услуг (§3c). */
export const SERVICE_LABELS: Record<Service, string> = {
  sout: 'СОУТ',
  training: 'Обучение',
  profrisk: 'Профриски',
  ecology: 'Экология',
}

/**
 * Варианты фильтра «Этап» списка заказов (orders-list-filters; ответ заказчицы 04.11 к п.8.1: фильтр
 * доступен и без выбора услуги). Выбрана услуга — её этапы. Без услуги — этапы всех `services` по
 * ключу: общие ключи («Заявка», «Договор» у СОУТ, обучения и профрисков) — одним вариантом без
 * услуги, свои этапы услуги — «СОУТ: Замеры», «Экология: Принят». Сервер ищет этап по ключу во
 * всех услугах.
 */
export function stageFilterOptions(
  service: Service | undefined,
  services: Service[],
): { key: string; label: string }[] {
  if (service) return STAGES_BY_SERVICE[service].map((st) => ({ key: st.key, label: st.label }))
  const owners = new Map<string, Service[]>()
  for (const s of services) {
    for (const st of STAGES_BY_SERVICE[s]) owners.set(st.key, [...(owners.get(st.key) ?? []), s])
  }
  const shared: { key: string; label: string }[] = []
  const own: { key: string; label: string }[] = []
  for (const s of services) {
    for (const st of STAGES_BY_SERVICE[s]) {
      const by = owners.get(st.key) ?? []
      if (by.length > 1) {
        if (by[0] === s) shared.push({ key: st.key, label: st.label })
      } else {
        own.push({ key: st.key, label: `${SERVICE_LABELS[s]}: ${st.label}` })
      }
    }
  }
  return [...shared, ...own]
}

export function serviceLabel(service: Service | string): string {
  return SERVICE_LABELS[service as Service] ?? service
}

/**
 * У заказа услуги есть регистрационный номер, который присваивает лаборатория и вводит команда на
 * карточке заказа: СОУТ (centrika-docs!73) и профриски (profrisk-order-registration-number). У
 * обучения рег.номера нет. Зеркало бэкенда — `domain.Service.HasRegistrationNumber`.
 */
export function hasRegistrationNumber(service: Service | string): boolean {
  return service === 'sout' || service === 'profrisk'
}

/** Русская плюрализация по числу: формы для 1 / 2–4 / 5–0 (с учётом 11–14). */
export function pluralRu(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few
  return many
}

/** Формы единицы размера заказа по услуге (D10): [одна, 2–4, много]. */
const COMPOSITION_FORMS: Record<Service, [string, string, string]> = {
  sout: ['рабочее место', 'рабочих места', 'рабочих мест'],
  training: ['слушатель', 'слушателя', 'слушателей'],
  profrisk: ['должность', 'должности', 'должностей'],
  ecology: ['площадка', 'площадки', 'площадок'],
}

/** Ярлык строки «Состав заказа» по услуге (D10) — как в референсе. */
export const COMPOSITION_ROW_LABELS: Record<Service, string> = {
  sout: 'Рабочих мест',
  training: 'Слушателей',
  profrisk: 'Должностей',
  ecology: 'Площадок',
}

/**
 * Вычисляемая сводка размера заказа (D10): «12 рабочих мест» / «1 слушатель» / «3 должности».
 * Используется в заголовке карточки и подписи в списке вместо свободного «объёма».
 */
export function compositionSummary(service: Service | string, count: number): string {
  const forms = COMPOSITION_FORMS[service as Service]
  if (!forms) return ''
  return `${count} ${pluralRu(count, forms[0], forms[1], forms[2])}`
}

/** Русские ярлыки статуса данных для КОМАНДЫ — полный путь (§3c). */
export const TEAM_STATUS_LABELS: Record<OrderDataStatus, string> = {
  draft: 'Черновик',
  filling: 'На заполнении',
  review: 'На проверке',
  changes_requested: 'Требуются правки',
  in_progress: 'В работе',
  done: 'Готово',
  cancelled: 'Отменён',
}

/** Подпись + цветовая роль статуса. */
export interface BadgeView {
  label: string
  role: StatusRole
}

/**
 * Клиентский бейдж: маппинг dataStatus → понятный клиенту ярлык + роль цвета (§3c).
 * draft→«Черновик»; filling→«На заполнении»; changes_requested→«Требуются правки»;
 * review|in_progress→«Выполняется»; done→«Завершён»; cancelled→«Отменён» (order-cancel-restore).
 * Черновик, «На заполнении» и «Требуются правки» — отдельные бейджи (от клиента ждут действия).
 */
export function clientBadge(status: OrderDataStatus): BadgeView {
  switch (status) {
    case 'draft':
      return { label: 'Черновик', role: 'neutral' }
    case 'filling':
      return { label: 'На заполнении', role: 'warning' }
    case 'changes_requested':
      return { label: 'Требуются правки', role: 'danger' }
    case 'review':
    case 'in_progress':
      return { label: 'Выполняется', role: 'info' }
    case 'done':
      return { label: 'Завершён', role: 'success' }
    case 'cancelled':
      return { label: 'Отменён', role: 'neutral' }
  }
}

/**
 * Командный бейдж: полный ярлык статуса + роль цвета (review — ждёт команду → warning; filling —
 * по заявке нужны договор и счёт, клиент довносит данные → warning).
 */
export function teamStatusBadge(status: OrderDataStatus): BadgeView {
  const role: StatusRole =
    status === 'review' || status === 'filling'
      ? 'warning'
      : status === 'changes_requested'
        ? 'danger'
        : status === 'in_progress'
          ? 'info'
          : status === 'done'
            ? 'success'
            : 'neutral'
  return { label: TEAM_STATUS_LABELS[status], role }
}

/** Число завершённых этапов степпера (isDone). */
export function doneStageCount(stages: Pick<StageDTO, 'isDone'>[]): number {
  return stages.filter((s) => s.isDone).length
}

/** Доступные клиенту действия по статусу заказа (для списка «Мои заказы»). */
export interface ClientOrderActions {
  /**
   * Заказ в фазе данных можно продолжить заполнять (draft|filling|changes_requested): действие
   * «Продолжить» ведёт прямо в анкету услуги (D9). Поданные заказы открываются на трекинг.
   */
  canContinue: boolean
  /** Удалять можно только свой черновик (защита «фотографии» данных; возвращённый уже подан). */
  canDelete: boolean
  /** Отправка/переотправка из draft|filling|changes_requested. */
  canResubmit: boolean
  /** Скачивание готовых файлов доступно у завершённого заказа. */
  canDownload: boolean
}

export function clientOrderActions(status: OrderDataStatus): ClientOrderActions {
  return {
    canContinue: status === 'draft' || status === 'filling' || status === 'changes_requested',
    canDelete: status === 'draft',
    canResubmit: status === 'draft' || status === 'filling' || status === 'changes_requested',
    canDownload: status === 'done',
  }
}

/** Маршрут анкеты услуги заказа (`/orders/:id/:service`). Сегмент пути = значение `service`. */
export function serviceAnketaPath(orderId: string, service: Service): string {
  return `/orders/${orderId}/${service}`
}

/**
 * Целевой маршрут клиентского заказа по фазе (D9): заказ в фазе данных
 * (draft|filling|changes_requested) ведёт прямо в анкету услуги; поданный заказ — на экран-трекинг
 * (`/orders/:id`). Единый источник правила для списка и переходов.
 */
export function clientOrderTarget(order: {
  id: string
  service: Service
  dataStatus: OrderDataStatus
}): string {
  return clientOrderActions(order.dataStatus).canContinue
    ? serviceAnketaPath(order.id, order.service)
    : `/orders/${order.id}`
}

/** Базовый набор типов документов (зеркало seed `document_types`; §documents). */
export interface DocTypeDef {
  code: string
  name: string
}

export const DOCUMENT_TYPES: DocTypeDef[] = [
  { code: 'certificates', name: 'Удостоверения' },
  { code: 'sout_reports', name: 'Отчёты СОУТ' },
  { code: 'profrisk_cards', name: 'Карты профрисков' },
  { code: 'contracts', name: 'Договоры' },
  { code: 'invoices', name: 'Счета' },
  { code: 'declarations', name: 'Декларации' },
  { code: 'eco_documents', name: 'Экология — проекты и нормативы' },
  { code: 'eco_reports', name: 'Экология — отчётность' },
]

/** Фильтр реестра по коду типа (null/'' → без фильтра, «Все типы»). */
export function filterByType<T extends { typeCode: string }>(
  docs: T[],
  typeCode: string | null | undefined,
): T[] {
  if (!typeCode) return docs
  return docs.filter((d) => d.typeCode === typeCode)
}

/** Русские ярлыки действий журнала (§3c, канон `action`). */
export const AUDIT_ACTION_LABELS: Record<string, string> = {
  order_created: 'Создан заказ',
  order_submitted: 'Заказ отправлен на проверку',
  order_requested: 'Отправлена заявка на обучение',
  order_returned: 'Заказ возвращён на доработку',
  order_accepted: 'Заказ принят в работу',
  order_completed: 'Заказ завершён',
  order_cancelled: 'Заказ отменён',
  order_restored: 'Заказ восстановлен',
  order_edited: 'Заказ отредактирован',
  stage_advanced: 'Сменён этап «Ход работ»',
  order_deleted: 'Заказ удалён',
  file_uploaded: 'Загружен файл',
  file_deleted: 'Удалён файл',
  client_created: 'Создан клиент',
  account_created: 'Создан аккаунт',
  account_disabled: 'Аккаунт отключён',
  account_enabled: 'Аккаунт включён',
  account_deleted: 'Аккаунт удалён',
  account_updated: 'Аккаунт: изменены ФИО или телефон',
  account_role_changed: 'Аккаунт: изменена роль',
  client_manager_set: 'Клиенту назначен менеджер',
  client_manager_cleared: 'У клиента снят менеджер',
  client_support_manager_set: 'Клиенту назначен менеджер по сопровождению',
  client_support_manager_cleared: 'У клиента снят менеджер по сопровождению',
  client_login_representative_set: 'Вход организации: изменён представитель',
  order_note_added: 'Заказ: внутренний комментарий',
  // «Мои вопросы» (client-questions; ТЗ п.9.7).
  question_created: 'Клиент задал вопрос',
  question_message_added: 'Клиент дополнил вопрос',
  question_answered: 'Ответ на вопрос клиента',
  question_closed: 'Клиент закрыл вопрос',
  // «Сопоставление» каталога заменило программу в заявке «На заполнении» (training-followups).
  order_program_replaced: 'Заказ: программа заменена по каталогу',
  account_assignments_transferred: 'Аккаунт: переданы клиенты и заказы',
  training_subscription_responsible_set: 'Обучение: задан ответственный по абонентскому обслуживанию',
  training_subscription_responsible_cleared:
    'Обучение: снят ответственный по абонентскому обслуживанию',
  sout_result_set: 'Внесён результат СОУТ',
  profrisk_result_set: 'Внесён результат профрисков',
  training_record_set: 'Запись обучения обновлена',
  sout_result_historical_set: 'Внесена историческая запись СОУТ',
  profrisk_result_historical_set: 'Внесена историческая запись профрисков',
  reminder_sent: 'Отправлено напоминание об обучении',
  program_created: 'Программа обучения создана',
  program_updated: 'Программа обучения изменена',
  program_deleted: 'Программа обучения удалена',
  // Каталог обучения v2 (training-catalog-v2) и подтверждение (training-packages-approval).
  program_archived: 'Программа обучения убрана в архив',
  program_restored: 'Программа обучения возвращена из архива',
  program_replaced: 'Программа обучения сопоставлена с новой',
  program_approved: 'Программа обучения подтверждена',
  program_rejected: 'Программа обучения отклонена',
  training_catalog_imported: 'Загружен файл каталога обучения',
  direction_created: 'Раздел каталога обучения добавлен',
  direction_updated: 'Раздел каталога обучения переименован',
  direction_deleted: 'Раздел каталога обучения удалён',
  direction_moved: 'Изменён порядок разделов каталога обучения',
  direction_journal_code_set: 'Разделу каталога обучения назначена колонка журнала',
  package_created: 'Пакет программ добавлен',
  package_updated: 'Пакет программ изменён',
  package_archived: 'Пакет программ убран в архив',
  package_restored: 'Пакет программ возвращён из архива',
  package_moved: 'Изменён порядок пакетов программ',
  package_approved: 'Пакет программ подтверждён',
  package_rejected: 'Пакет программ отклонён',
  package_deleted: 'Пакет программ удалён',
  eco_catalog_item_created: 'Экология: позиция справочника добавлена',
  eco_catalog_item_updated: 'Экология: позиция справочника изменена',
  eco_catalog_item_deleted: 'Экология: позиция справочника удалена',
  eco_catalog_card_updated: 'Экология: изменены поля карточки позиции',
  eco_catalog_order_fields_updated: 'Экология: изменены поля заявки позиции',
  eco_fkko_imported: 'Экология: загружен справочник ФККО',
  eco_matrix_updated: 'Экология: изменена матрица «Кому что нужно»',
  eco_calendar_updated: 'Экология: изменён календарь отчётности',
  eco_settings_updated: 'Экология: изменены настройки раздела',
  eco_site_created: 'Экология: площадка добавлена',
  eco_site_updated: 'Экология: площадка изменена',
  eco_site_deleted: 'Экология: площадка удалена',
  eco_item_saved: 'Экология: внесены данные документа или отчёта',
  eco_item_dismissed: 'Экология: строка убрана из раздела',
  eco_survey_completed: 'Экология: пройден опрос по площадке',
  eco_change_check: 'Экология: ответ «что менялось»',
  eco_order_result_set: 'Экология: внесён результат по площадке заказа',
  eco_question_created: 'Экология: вопрос опросника добавлен',
  eco_question_updated: 'Экология: вопрос опросника изменён',
  eco_question_deleted: 'Экология: вопрос опросника удалён',
  eco_rule_created: 'Экология: правило опроса добавлено',
  eco_rule_updated: 'Экология: правило опроса изменено',
  eco_rule_deleted: 'Экология: правило опроса удалено',
  eco_subscription_changed: 'Экология: изменено абонентское обслуживание',
  training_subscription_set: 'Обучение: задан срок абонентского обслуживания',
  training_subscription_removed: 'Обучение: абонентское обслуживание снято',
  eco_responsible_saved: 'Экология: изменён ответственный за экологию',
  position_created: 'Должность создана',
  position_updated: 'Должность переименована',
  position_deleted: 'Должность удалена',
  position_verified: 'Должность отмечена проверенной',
  position_unverified: 'С должности снята отметка «проверена»',
  position_hidden: 'Должность скрыта из подсказок',
  position_unhidden: 'Должность снова показывается в подсказках',
  position_merged: 'Должности объединены',
  template_updated: 'Шаблон письма изменён',
  broadcast_sent: 'Отправлена ручная рассылка',
  person_deactivated: 'Сотрудник деактивирован',
  person_activated: 'Сотрудник активирован',
  structure_imported: 'Импортирована структура из Excel',
  company_profile_updated: 'Изменены реквизиты компании',
  dadata_autofill: 'Автозаполнение по ИНН (DaData)',
  /** Создание аккаунта СОТРУДНИКА ЦЕНТРИКИ (не путать с сотрудником компании-клиента). */
  employee_created: 'Создан аккаунт сотрудника Центрики',
  client_credentials_updated: 'Изменены данные входа клиента',
  client_login_linked: 'Вход закреплён за организацией',
  client_login_unlinked: 'Вход откреплён от организации',
  manual_reminder_sent: 'Отправлено ручное напоминание',
}

/** Ярлык действия журнала; безопасный фолбэк для неизвестных кодов — сам код (D4). */
export function auditActionLabel(action: string): string {
  return AUDIT_ACTION_LABELS[action] ?? action
}

/**
 * Русские ярлыки типа объекта журнала (D3) — фолбэк для колонки «Объект», когда у записи
 * нет владельца-клиента (`ownerName`), например аккаунты команды, программы, рассылки.
 */
export const AUDIT_OBJECT_TYPE_LABELS: Record<string, string> = {
  order: 'Заказ',
  client: 'Клиент',
  account: 'Аккаунт',
  company: 'Компания',
  file: 'Файл',
  program: 'Программа обучения',
  program_package: 'Пакет программ',
  training_direction: 'Раздел каталога обучения',
  position: 'Должность',
  eco_catalog_item: 'Справочник экологии',
  eco_settings: 'Настройки экологии',
  eco_site: 'Площадка (экология)',
  eco_site_item: 'Документ или отчёт (экология)',
  eco_change_check: 'Изменения на предприятии (экология)',
  eco_question: 'Вопрос опросника (экология)',
  eco_rule: 'Правило опроса (экология)',
  training_record: 'Запись обучения',
  person: 'Сотрудник',
  broadcast: 'Рассылка',
  training: 'Обучение',
  email_template: 'Шаблон письма',
  question: 'Вопрос клиента',
}

/**
 * Осмысленная подпись объекта записи журнала (D3): приоритет — имя владельца (клиент/компания),
 * иначе — русский ярлык типа объекта; фолбэк для неизвестного типа — сам код (не ломает журнал).
 */
export function auditObjectLabel(a: { objectType: string; ownerName?: string }): string {
  return a.ownerName ?? AUDIT_OBJECT_TYPE_LABELS[a.objectType] ?? a.objectType
}

/**
 * Человекочитаемый текст деталей записи журнала (D2). Ветвление идёт по ФОРМЕ `details`
 * (набор/тип ключей), а НЕ по `action` — поэтому `action` не параметризует логику (`_action`
 * оставлен в сигнатуре как контекст для будущих action-специфичных форматов и симметрии вызова).
 * Формы см. у writer'ов `InsertAudit`: `{action}` — общий audit()-хелпер, `{file_id}` — файловые
 * операции, `{recipients,subject?}` — рассылки/напоминания. ГАРАНТИРОВАННО никогда не возвращает
 * сериализацию объекта (`String(object)`/`[object Object]`) — вложенные объекты/массивы в фолбэке
 * проходят через явный `JSON.stringify`, не через `String()`; пустой (`{}` — частый случай, т.к.
 * колонка jsonb `NOT NULL DEFAULT '{}'`) или самоочевидный/избыточный объект → '' — тогда колонка
 * «Объект» ничего не дописывает.
 */
export function auditDetailsText(
  action: string,
  details: AuditDetails | null | undefined,
): string {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return ''
  const keys = Object.keys(details)
  if (keys.length === 0) return ''

  // `{action}` — самотег общего audit()-хелпера команды: дублирует уже показанный ярлык действия.
  if (keys.length === 1 && keys[0] === 'action') return ''
  // `{file_id}` — дублирует тип объекта «Файл», уже показанный в колонке «Объект».
  if (keys.length === 1 && keys[0] === 'file_id') return ''

  // Объединение должностей (positions-dictionary-review): какая в какую и сколько мест перенесено.
  if (action === 'position_merged' && typeof details.sourceName === 'string' && typeof details.targetName === 'string') {
    const moved = typeof details.workplaces === 'number' ? details.workplaces : 0
    return `«${details.sourceName}» → «${details.targetName}» · рабочих мест: ${moved}`
  }

  // Закрепление и открепление входа за организацией: почта входа (идентификатор входа в журнале не нужен).
  if (
    (action === 'client_login_linked' || action === 'client_login_unlinked') &&
    typeof details.email === 'string'
  ) {
    return details.email
  }

  // Отмена и восстановление заказа (order-cancel-restore): кто, из какого статуса и почему.
  if (action === 'order_cancelled') {
    const who = details.byClient === true ? 'клиентом' : 'Центрикой'
    const from =
      typeof details.fromStatus === 'string' && details.fromStatus in TEAM_STATUS_LABELS
        ? ` · был «${TEAM_STATUS_LABELS[details.fromStatus as OrderDataStatus]}»`
        : ''
    const reason =
      typeof details.reason === 'string' && details.reason ? ` · Причина: ${details.reason}` : ''
    return `Отменён ${who}${from}${reason}`
  }
  if (action === 'order_restored') {
    const who = details.byClient === true ? 'клиентом' : 'Центрикой'
    const to =
      typeof details.toStatus === 'string' && details.toStatus in TEAM_STATUS_LABELS
        ? ` · статус «${TEAM_STATUS_LABELS[details.toStatus as OrderDataStatus]}»`
        : ''
    return `Восстановлен ${who}${to}`
  }

  // Рассылки/напоминания: количество получателей (+ тема письма, если есть).
  if (typeof details.recipients === 'number') {
    const count = details.recipients
    const noun = pluralRu(count, 'получатель', 'получателя', 'получателей')
    const subject =
      typeof details.subject === 'string' && details.subject ? ` · Тема: «${details.subject}»` : ''
    return `${count} ${noun}${subject}`
  }

  // Фолбэк для незнакомых форм: компактный перечень примитивных полей — никогда сериализация
  // вложенного объекта/массива через toString (только явный JSON.stringify, если это нужно).
  const parts: string[] = []
  for (const key of keys) {
    if (key === 'action') continue
    const value = details[key]
    if (value === null || value === undefined) continue
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      parts.push(`${key}: ${value}`)
    } else {
      parts.push(`${key}: ${JSON.stringify(value)}`)
    }
  }
  return parts.join(', ')
}
